import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  styled as muiStyled,
  css,
} from '@mui/material';
import React from 'react';

import type { TableRowData } from './generic-table';

import { getDesignTokens } from '~/client/components/base/core';
import { spc } from '~/client/components/base/spacer';

// Generic version of TableColumn for GenericTableNew
export interface TableColumn<T = any> {
  key: string;
  label: string | React.ReactNode;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  /**
   * Custom render function for cell content
   * Receives: value (from row[column.key]), row (entire row object), index (row index)
   */
  render?: (value: any, row: T, index: number) => React.ReactNode;
  /**
   * Custom render function for header
   * Receives: column (the column definition)
   */
  renderHeader?: (column: TableColumn<T>) => React.ReactNode;
  /**
   * Conditional column visibility
   * Receives: data (all rows) - return true to show column
   */
  show?: (data: T[]) => boolean;
  /**
   * Dynamic cell className
   * Can be a string or a function that receives row and index
   */
  cellClassName?: string | ((row: T, index: number) => string);
  /**
   * Dynamic cell style
   * Can be a CSSProperties object or a function that receives row and index
   */
  cellStyle?:
    | React.CSSProperties
    | ((row: T, index: number) => React.CSSProperties);
}

export type { TableRowData };

export interface GenericTableNewProps<T = any> {
  columns: TableColumn<T>[];
  data: T[];
  className?: string;
  /**
   * Custom CSS class for the table container
   */
  containerClassName?: string;
  /**
   * Whether to show table headers
   */
  showHeaders?: boolean;
  /**
   * Custom header styling class
   */
  headerClassName?: string;
  /**
   * Custom cell styling class (applies to all cells if cellClassName not provided per column)
   */
  cellClassName?: string;
  /**
   * Whether to use Paper component as container
   */
  usePaper?: boolean;
  /**
   * Function to extract unique key for each row
   * Defaults to using index if not provided
   */
  getRowKey?: (row: T, index: number) => string | number;
  /**
   * Row click handler
   * Receives: row (entire row object), index (row index)
   */
  onRowClick?: (row: T, index: number) => void;
  /**
   * Dynamic row className
   * Can be a string or a function that receives row and index
   */
  rowClassName?: string | ((row: T, index: number) => string);
  /**
   * Dynamic row style
   * Can be a CSSProperties object or a function that receives row and index
   */
  rowStyle?:
    | React.CSSProperties
    | ((row: T, index: number) => React.CSSProperties);
  /**
   * @deprecated Outer borders are always shown. This prop is kept for backward compatibility but has no effect.
   */
  showOuterBorders?: boolean;
  /**
   * Show vertical borders between columns
   * Default: false (no column dividers)
   */
  showColumnBorders?: boolean;
  /**
   * Show horizontal borders between rows
   * Default: true (subtle row dividers)
   */
  showRowBorders?: boolean;
  /**
   * Header background style
   * Default: 'light' (light gray background)
   */
  headerBackground?: 'light' | 'dark';
}

const GenericTableNewImpl = <T extends TableRowData = TableRowData>({
  columns,
  data,
  className,
  containerClassName,
  showHeaders = true,
  headerClassName,
  cellClassName,
  usePaper = false,
  getRowKey,
  onRowClick,
  rowClassName,
  rowStyle,
  showOuterBorders = true, // Deprecated: borders are always shown now
  showColumnBorders = false,
  showRowBorders = true,
  headerBackground = 'light',
}: GenericTableNewProps<T>) => {
  const theme = useTheme();
  const t = getDesignTokens(
    theme.palette.mode as 'light' | 'dark',
    theme.accentPalette
  );
  const Container = usePaper ? Paper : 'div';
  const containerProps = usePaper ? { component: Paper } : {};

  // Filter columns based on show function
  const visibleColumns = columns.filter((column) => {
    if (column.show) {
      return column.show(data);
    }
    return true;
  });

  // Get row key function
  const getKey = getRowKey ?? ((_row: T, index: number) => index);

  // Get row className
  const getRowClass = (row: T, index: number): string => {
    if (typeof rowClassName === 'function') {
      return rowClassName(row, index);
    }
    return rowClassName ?? '';
  };

  // Get row style
  const getRowStyles = (row: T, index: number): React.CSSProperties => {
    if (typeof rowStyle === 'function') {
      return rowStyle(row, index);
    }
    return rowStyle ?? {};
  };

  // Get cell className
  const getCellClass = (
    column: TableColumn<T>,
    row: T,
    index: number
  ): string => {
    if (column.cellClassName) {
      if (typeof column.cellClassName === 'function') {
        return column.cellClassName(row, index);
      }
      return column.cellClassName;
    }
    return cellClassName ?? '';
  };

  // Get cell style
  const getCellStyles = (
    column: TableColumn<T>,
    row: T,
    index: number
  ): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      width: column.width,
      textAlign: column.align ?? 'left',
    };

    if (column.cellStyle) {
      if (typeof column.cellStyle === 'function') {
        return { ...baseStyle, ...column.cellStyle(row, index) };
      }
      return { ...baseStyle, ...column.cellStyle };
    }

    return baseStyle;
  };

  // Render cell content
  const renderCellContent = (column: TableColumn<T>, row: T, index: number) => {
    if (column.render) {
      const value = (row as any)[column.key];
      return column.render(value, row, index);
    }
    // Backward compatibility: fallback to row[column.key]
    return (row as any)[column.key];
  };

  // Render header
  const renderHeader = (column: TableColumn<T>) => {
    if (column.renderHeader) {
      return column.renderHeader(column);
    }
    return column.label;
  };

  return (
    <TableContainer
      {...containerProps}
      className={`${containerClassName ? containerClassName : ''} generic-table-new-container`}
      sx={{
        margin: 0,
        overflow: 'hidden',
        borderRadius: `${t.radiusSmall}px`,
        width: '100%',
        border: `1px solid ${t.borderDefault}`,
        boxShadow: t.shadowSoft,
      }}
    >
      <Table className={className}>
        {showHeaders && (
          <TableHead>
            <TableRow>
              {visibleColumns.map((column) => {
                const headerStyle = {
                  width: column.width,
                  textAlign: column.align ?? 'left',
                  ...(showRowBorders
                    ? { borderBottom: `1px solid ${t.borderDefault}` }
                    : {}),
                };

                return (
                  <TableCell
                    key={column.key}
                    className={`${headerClassName ? headerClassName : ''} generic-table-new-header`}
                    style={headerStyle}
                  >
                    {renderHeader(column)}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {data.map((row, rowIndex) => {
            const rowKey = getKey(row, rowIndex);
            const rowClass = getRowClass(row, rowIndex);
            const rowStyles = getRowStyles(row, rowIndex);

            return (
              <TableRow
                key={rowKey}
                className={rowClass || undefined}
                style={rowStyles}
                onClick={
                  onRowClick ? () => onRowClick(row, rowIndex) : undefined
                }
                sx={onRowClick ? { cursor: 'pointer' } : undefined}
              >
                {visibleColumns.map((column) => {
                  const cellClass = getCellClass(column, row, rowIndex);
                  const cellStyles = getCellStyles(column, row, rowIndex);

                  // Add border-bottom style when showRowBorders is enabled
                  const borderStyle = showRowBorders
                    ? {
                        ...cellStyles,
                        borderBottom: `1px solid ${t.borderDefault}`,
                      }
                    : cellStyles;

                  return (
                    <TableCell
                      key={column.key}
                      className={`${cellClass ? cellClass : ''} generic-table-new-cell`}
                      style={borderStyle}
                    >
                      {renderCellContent(column, row, rowIndex)}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export const GenericTableNew = muiStyled(
  GenericTableNewImpl as React.FC<GenericTableNewProps>
)<GenericTableNewProps>(({ theme, showColumnBorders, headerBackground }) => {
  const t = getDesignTokens(
    theme.palette.mode as 'light' | 'dark',
    theme.accentPalette
  );
  return css`
    /* Table base styles */
    .generic-table-new-container .MuiTable-root {
      border-collapse: collapse;
      width: 100%;
      table-layout: fixed;
    }

    /* Header styles */
    .generic-table-new-header {
      padding: ${theme.spacing(3)};
      color: ${t.textPrimary};
      font-weight: 600;
      font-size: 14px;
      vertical-align: middle;
      line-height: 1.4;
    }

    /* Column borders */
    ${showColumnBorders &&
    `
        .generic-table-new-header {
          border-left: 1px solid ${t.borderDefault};
          border-right: 1px solid ${t.borderDefault};
        }

        .generic-table-new-cell {
          border-left: 1px solid ${t.borderDefault};
          border-right: 1px solid ${t.borderDefault};
        }

        .MuiTableRow-root .MuiTableCell-root:first-child {
          border-left: none;
        }

        .MuiTableRow-root .MuiTableCell-root:last-child {
          border-right: none;
        }
      `}

    /* Cell styles - theme-aware for light/dark mode */
      .generic-table-new-cell {
      padding: ${theme.spacing(3)};
      vertical-align: middle;
      font-size: 14px;
      line-height: 1.4;
      background-color: ${t.surface1};
      color: ${t.textPrimary};
    }

    /* Print styles for tables - scoped to this component only */
    @media print {
      .generic-table-new-container {
        box-shadow: none;
        border-radius: 0;
        margin: 8px 0;
      }

      .generic-table-new-container .MuiTableCell-root {
        border: 1px solid ${t.textPrimary} !important;
        padding: 6px !important;
      }

      .generic-table-new-container .MuiTable-root {
        border-collapse: collapse !important;
      }

      .generic-table-new-container .MuiTableHead-root .MuiTableCell-root,
      .generic-table-new-container .MuiTableBody-root .MuiTableCell-root {
        border: 1px solid ${t.textPrimary} !important;
      }

      .generic-table-new-header {
        background-color: ${t.surface2} !important;
        color: ${t.textPrimary} !important;
        font-weight: 600 !important;
      }

      ${headerBackground === 'dark' &&
      `
          .generic-table-new-header {
            background-color: ${t.brand.primary} !important;
            color: ${t.brand.primaryContrast} !important;
          }
        `}

      .generic-table-new-cell {
        background-color: ${t.surface1} !important;
        color: ${t.textPrimary} !important;
      }
    }
  `;
});
